/* eslint-disable no-unused-expressions */
/* eslint-disable consistent-return */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { withFormatter } from '@concur/nui-intl-runtime';
import { lowerCase } from '@concur/core-ui-shell';

const Menu = (props) => {
    const {
        appHeaderWidth,
        ariaLabel,
        children,
        collapsible,
        parentMenuCssBlock,
        formatter,
        id,
        isExpandedSideNav,
        isTopLevelMenu,
        utilityWidth,
        onCollapse,
        menuButtonRef,
        isSideNavOverlay,
        disableFocusTrap,
        /* eslint-disable react/prop-types */
        lang,
        /* eslint-enable react/prop-types */
        ...otherProps
    } = props;

    const [focusableMenuItems, setFocusableMenuItems] = useState([]);
    const [activeItem, setActiveItem] = useState();
    const setRefs = useRef(new Map()).current;
    // TODO: this likely needs the other menu items like joule and translation tool
    const focusableItemSelector = 'a, button, span[tabindex="0"], span[role="button"], #helpPortalMenu';

    const listRef = useRef(null);

    const getAllMenuItems = () => {
        const menu = document.querySelector(`[class*="${parentMenuCssBlock}"]`);

        if (!menu) {
            return [];
        }

        return Array.prototype.slice.call(menu.querySelectorAll('a, [role="button"]'));
    };

    const getFocusableItems = (listItems) => {
        const focusableItems = [];
        listItems.forEach((item) => {
            const itemVisible = item.getAttribute('aria-hidden') !== 'true';
            const focusableItem = item.querySelector(focusableItemSelector);
            itemVisible && focusableItem && focusableItems.push(focusableItem);
        });
        return focusableItems;
    };

    // returns all currently focused item's submenu items
    const getFocusableSubMenuItems = () => {
        const focusedElement = document.activeElement;

        if (!focusedElement || !focusedElement.nextSibling) {
            return [];
        }

        const siblingMenu = focusedElement.nextSibling.children;
        // return the active element's sibling <ul>'s children
        return siblingMenu?.length
            ? getFocusableItems(Array.prototype.slice.call(siblingMenu))
            : [];
    };

    // returns all currently focused item's parent menu's items
    const getFocusableParentMenuItems = () => {
        // make sure we aren't in a dialog or on the menu item that has
        // triggered a dialog.  if so, that no longer obeys the
        // default menu keyboard handling
        let isDialog = false;
        let innerMenu;
        let currentElement = document.activeElement;
        if (
            currentElement.getAttribute('aria-haspopup') !== 'dialog'
            || currentElement.getAttribute('aria-expanded') !== 'true'
        ) {
            // current element does not have a popup or popup is not expanded
            // now check if current element is within a dialog
            while (currentElement && currentElement.getAttribute('role') !== 'dialog') {
                currentElement = currentElement.parentElement;
            }
            if (currentElement) {
                // if in a dialog, check if there is an inner menu to traverse
                isDialog = true;
                innerMenu = currentElement.querySelector('[class*="submenu"]');
            }
        } else {
            isDialog = true;
        }

        const activeItemParent = isDialog ? innerMenu : listRef?.current;

        if (!activeItemParent) {
            return [];
        }

        const parentMenuChildren = activeItemParent.children;
        // return the active element's parent <li>'s parent <ul>'s children
        return parentMenuChildren.length
            ? getFocusableItems(Array.prototype.slice.call(parentMenuChildren))
            : [];
    };

    const hasParentNav = () => {
        const ul = listRef?.current;

        return ul?.parentElement?.tagName === 'NAV';
    };

    useEffect(() => {
        const menuItems = isExpandedSideNav ? getAllMenuItems() : getFocusableSubMenuItems();
        switch (activeItem) {
        case null: // focus already set after closing menu; no action needed
            break;

        default:
            if (menuItems) {
                if (!menuItems.includes(document.activeElement)) {
                    menuItems[0]?.focus();
                }
            }
            break;
        }
    }, [activeItem]);

    const onSetActiveItem = (item) => {
        React.Children.forEach(children, (child) => {
            const childRef = setRefs.get(child?.key);
            if (childRef?.contains?.(item)) {
                // if contains submenu
                if (item?.nextSibling) {
                    setActiveItem(child?.key);
                }
            }
        });
    };

    const focusDown = (e) => {
        const menuItems = isExpandedSideNav ? getAllMenuItems() : getFocusableParentMenuItems();
        if (menuItems) {
            const activeIndex = menuItems.indexOf(document.activeElement);
            // focus on next item in list
            if (activeIndex !== menuItems.length - 1 && activeIndex !== -1) {
                menuItems[activeIndex + 1]?.focus();
                // after reaching the end of the items, focus on first item
            } else {
                // if focus trap is disabled, do not focus first item if tab key is pressed
                if (disableFocusTrap && e.key === 'Tab') return;
                menuItems[0]?.focus();
            }
        }
        e.preventDefault();
        e.stopPropagation();
    };

    const focusUp = (e) => {
        const menuItems = isExpandedSideNav ? getAllMenuItems() : getFocusableParentMenuItems();
        if (menuItems) {
            const activeIndex = menuItems.indexOf(document.activeElement);
            // focus on last item in list
            if (activeIndex <= 0) {
                // if focus trap is disabled, do not focus last item if tab key is pressed
                if (disableFocusTrap && e.key === 'Tab') return;
                menuItems[menuItems.length - 1]?.focus();
                // focus on previous item
            } else {
                menuItems[activeIndex - 1]?.focus();
            }
        }
        e.preventDefault();
        e.stopPropagation();
    };

    const closeMenu = (e) => {
        if (activeItem) {
            setActiveItem(null);
            const parentMenuItem = document.activeElement?.parentElement
                ?.parentElement?.parentElement?.children[0];
            parentMenuItem?.focus();
            e.stopPropagation();
        } else if (isSideNavOverlay) {
            menuButtonRef.current?.focus();
            e.stopPropagation();
        }
    };

    const handleFocus = () => {
        if (!focusableMenuItems || focusableMenuItems.length < 1) {
            const menuItems = isExpandedSideNav ? getAllMenuItems() : getFocusableParentMenuItems();
            setFocusableMenuItems(menuItems);
        }
    };

    const handleKeyDown = (e) => {
        switch (e.key) {
        case 'Escape':
            // close menu on escape key
            closeMenu(e);
            onCollapse?.();
            // if its an expanded side nav, escape key should return focus to menu button
            if (isExpandedSideNav) {
                menuButtonRef.current?.focus();
            }
            break;
        case 'Tab':
            // do not focus trap when tabbing through top-level menu items
            if (!isTopLevelMenu && !hasParentNav()) {
                if (e.shiftKey) {
                    focusUp(e);
                } else {
                    focusDown(e);
                }
            }
            break;
        case 'ArrowDown':
            e.preventDefault();
            focusDown(e);
            break;
        case 'ArrowRight':
            e.preventDefault();
            break;
        case 'ArrowUp':
            e.preventDefault();
            focusUp(e);
            break;
        case 'ArrowLeft':
            e.preventDefault();
            break;
        default:
            break;
        }
    };

    const handleMouseEnter = (e) => {
        if (!isExpandedSideNav) {
            onSetActiveItem(e.target);
        }
    };

    const handleMouseLeave = (e) => {
        if (!isExpandedSideNav) {
            closeMenu(e);
        }
    };

    const triggerClick = (e) => {
        // one special case: don't let the menu intercept the space key
        // for checkboxes
        if (
            e.key === ' '
            && document.activeElement?.tagName === 'INPUT'
            && document.activeElement?.getAttribute('type') === 'checkbox'
        ) {
            e?.target?.click();
            return;
        }
        // open sub-submenu, focus on first element
        if (document.activeElement?.nextSibling) {
            onSetActiveItem(document.activeElement);
            // trigger click on item
        } else {
            e?.target?.click();
        }
    };

    const renderChildren = () => React.Children.map(children, (child) => {
        if (!child) return;

        const childProps = {
            expanded: child?.key && activeItem && activeItem === child?.key,
            onKeyDown: (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    triggerClick(e);
                }
            },
            onMouseEnter: handleMouseEnter,
        };

        // fragments need to be traversed manually
        if (child?.key?.includes('fragment')) {
            return React.cloneElement(
                child,
                {
                    ...child.props,
                    isExpandedSideNav,
                },
                React.Children.map(child.props.children, (fragmentChild) => {
                    if (fragmentChild) {
                        return React.cloneElement(fragmentChild, {
                            ...childProps,
                            isExpandedSideNav,
                            parentMenuCssBlock,
                        });
                    }
                }),
            );
        }
        // add ref based on child key to other MenuItems
        // hide or show based on presence in subItems
        return React.cloneElement(child, {
            ...childProps,
            isExpandedSideNav,
            parentMenuCssBlock,
        });
    });

    return (
        <ul
            {...otherProps}
            id={id}
            aria-label={ariaLabel}
            data-test={id ? `menu__list-${lowerCase(id)}` : null}
            onFocus={handleFocus}
            onKeyDown={handleKeyDown}
            ref={listRef}
            role="menubar"
            onMouseLeave={handleMouseLeave}
        >
            {renderChildren()}
        </ul>
    );
};

Menu.displayName = 'Menu';

Menu.propTypes = {
    appHeaderWidth: PropTypes.number,
    ariaLabel: PropTypes.string,
    className: PropTypes.string,
    children: PropTypes.node,
    collapsible: PropTypes.bool,
    id: PropTypes.string,
    isExpandedSideNav: PropTypes.bool,
    isTopLevelMenu: PropTypes.bool,
    disableFocusTrap: PropTypes.bool,
    parentMenuCssBlock: PropTypes.string,
    utilityWidth: PropTypes.number,
    onCollapse: PropTypes.func,
    menuButtonRef: PropTypes.object,
    isSideNavOverlay: PropTypes.bool,
};

export default withFormatter(Menu);
